import { useEffect, useRef } from "react";
import { MAX_HOTKEY_POSITION } from "../constants";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";

const G_KEY_CODE = "KeyG";
const SHOW_ALL_DIGIT_CODE = "Digit0";
const DIGIT_CODE_PATTERN = /^Digit([1-9])$/;

export interface CommunitySwitcherHotkeyEntry {
  readonly community: CommunitySwitcher;
  readonly position: number;
}

export interface UseCommunitySwitcherHotkeysOptions {
  readonly entries: readonly CommunitySwitcherHotkeyEntry[];
  readonly onActivate: (community: CommunitySwitcher) => void;
  readonly onShowAll?: () => void;
}

const isFormElementTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tagName = target.tagName;
  return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
};

export const useCommunitySwitcherHotkeys = ({
  entries,
  onActivate,
  onShowAll,
}: UseCommunitySwitcherHotkeysOptions) => {
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  const onShowAllRef = useRef(onShowAll);
  onShowAllRef.current = onShowAll;

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const downCodes = new Set<string>();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      downCodes.add(event.code);

      if (!event.altKey || !downCodes.has(G_KEY_CODE)) {
        return;
      }
      if (isFormElementTarget(event.target)) {
        return;
      }

      if (event.code === SHOW_ALL_DIGIT_CODE) {
        if (!onShowAllRef.current) {
          return;
        }
        event.preventDefault();
        onShowAllRef.current();
        return;
      }

      const digitMatch = DIGIT_CODE_PATTERN.exec(event.code);
      if (!digitMatch) {
        return;
      }
      const position = Number(digitMatch[1]);
      if (position > MAX_HOTKEY_POSITION) {
        return;
      }

      const match = entriesRef.current.find(
        entry => entry.position === position,
      );
      if (!match) {
        return;
      }

      event.preventDefault();
      onActivateRef.current(match.community);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      downCodes.delete(event.code);
    };

    const handleBlur = () => {
      downCodes.clear();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    globalThis.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      globalThis.removeEventListener("blur", handleBlur);
    };
  }, []);
};
